<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          sm="12"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="customerProfileRef"
      :items="customerProfileList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_INTERNET_BANKING_USERS')"
              @click="onSelectProductProfileItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_INTERNET_BANKING_USERS')"
              @click="onSelectProductProfileItem(data.item, $event.target, 'view')"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_IB_PROFILE' : 'ACTIVATE_IB_PROFILE')"
              @click="productProfileStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.status === 'Active' ? $t('Deactivate') : $t('Activate')
              }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="resetPasswordCount(data.item)">
              <feather-icon icon="UnlockIcon" />
              <span class="align-middle ml-50"> {{ $t('Reset Password Count') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="xl"
      no-close-on-backdrop
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <div>
        <form-wizard
          ref="individualCustomerForm"
          :title="null"
          :subtitle="null"
          layout="vertical"
          :back-button-text="$t('Previous')"
          :next-button-text="$t('Next')"
          class="wizard-vertical"
        >
          <!-- Customer Information tab -->
          <tab-content :title="$t('Customer Information')">
            <validation-observer ref="enrollCustomerProfileRules">
              <b-form>
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-1">
                  <h5 class="section-label mb-0">
                    {{ $t('Customer Information') }}
                  </h5>

                  <b-button
                    v-if="!isViewOnly"
                    v-b-tooltip.hover.top="$t('Sync latest customer records')"
                    variant="primary"
                    :disabled="isSyncingCustomerDetails"
                    @click="resyncCustomerDetails()"
                  >
                    {{ $t('Resync Customer Details') }}
                    <b-spinner
                      v-if="isSyncingCustomerDetails"
                      small
                      class="ml-50"
                    />
                    <feather-icon
                      v-else
                      icon="RotateCwIcon"
                    />
                  </b-button>
                </div>

                <b-row class="match-height">
                  <!-- Customer ID -->
                  <b-col md="6">
                    <InputField
                      id="customer-id"
                      v-model="customerProfileFormData.customerID"
                      :label="$t('Customer ID')"
                      :placeholder="$t('Enter customer ID')"
                      type="text"
                      for="customer-id"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!-- Customer ID -->
                  <b-col md="6">
                    <SelectField
                      id="title"
                      v-model="customerProfileFormData.title"
                      :label="$t('Title')"
                      :options="titleOptions"
                      :reduce="option => option.value"
                      item="label"
                      :placeholder="$t('Select')"
                      :readonly="isEditable"
                    />
                  </b-col>

                  <!-- Last Name -->
                  <b-col md="6">
                    <InputField
                      id="last-name"
                      v-model="customerProfileFormData.lastName"
                      :label="$t('Last Name')"
                      :placeholder="$t('Enter last name')"
                      type="text"
                      :error-name="$t('Last Name')"
                      rules="required"
                      onpaste="return false"
                      :is-allow-special-characters="false"
                      readonly="isEditable"
                    />
                    <p
                      v-if="syncedFields.lastName"
                      class="sync-info"
                      :class="syncedFields.lastName === 'synced' ? 'text-success' : ''"
                    >
                      {{ syncedFields.lastName === 'synced' ? 'Last Name synced successfully' : '' }}
                    </p>
                  </b-col>
                  <!-- Last Name -->

                  <!-- First Name -->
                  <b-col md="6">
                    <InputField
                      id="first-name"
                      v-model="customerProfileFormData.firstName"
                      :label="$t('First Name')"
                      :placeholder="$t('Enter first name')"
                      type="text"
                      :error-name="$t('First Name')"
                      rules="required"
                      onpaste="return false"
                      :is-allow-special-characters="false"
                      :readonly="isEditable"
                    />
                    <p
                      v-if="syncedFields.firstName"
                      class="sync-info"
                      :class="syncedFields.firstName === 'synced' ? 'text-success' : ''"
                    >
                      {{ syncedFields.firstName === 'synced' ? 'First Name synced successfully' : '' }}
                    </p>
                  </b-col>
                  <!-- First Name -->

                  <!-- Other Name -->
                  <b-col md="6">
                    <InputField
                      id="other-names"
                      v-model="customerProfileFormData.otherNames"
                      :label="$t('Other Names')"
                      :placeholder="$t('Enter other names')"
                      type="text"
                      :error-name="$t('Other Names')"
                      onpaste="return false"
                      :is-allow-special-characters="false"
                      readonly="isEditable"
                    />
                    <p
                      v-if="syncedFields.otherNames"
                      class="sync-info"
                      :class="syncedFields.otherNames === 'synced' ? 'text-success' : ''"
                    >
                      {{ syncedFields.otherNames === 'synced' ? 'Other Name synced successfully' : '' }}
                    </p>
                  </b-col>
                  <!-- Other Name -->

                  <!-- Date of Birth -->
                  <b-col md="6">
                    <InputField
                      id="date-of-birth"
                      v-model="customerProfileFormData.dateOfBirth"
                      :label="$t('Date of Birth')"
                      :placeholder="$t('Date of Birth')"
                      type="text"
                      :readonly="isEditable"
                    />
                    <p
                      v-if="syncedFields.dateOfBirth"
                      class="sync-info"
                      :class="syncedFields.dateOfBirth === 'synced' ? 'text-success' : ''"
                    >
                      {{ syncedFields.dateOfBirth === 'synced' ? 'Date of Birth synced successfully' : '' }}
                    </p>
                  </b-col>
                  <!--  Date of Birth -->

                  <!-- Phone Number -->
                  <b-col md="6">
                    <div class="d-flex align-items-end gap-1">
                      <div class="flex-grow-1">
                        <InputField
                          id="phone-number"
                          v-model="customerProfileFormData.phoneNumber"
                          :label="$t('Phone Number')"
                          :placeholder="$t('Enter phone number')"
                          type="number"
                          :readonly="isEditable"
                        />
                        <p
                          v-if="syncedFields.phoneNumber"
                          class="sync-info"
                          :class="syncedFields.phoneNumber === 'synced' ? 'text-success' : ''"
                        >
                          {{ syncedFields.phoneNumber === 'synced' ? 'Phone Number synced successfully' : '' }}
                        </p>
                      </div>
                    </div>
                  </b-col>
                  <!-- Phone Number -->
                  <!-- Email -->
                  <b-col md="6">
                    <div class="d-flex align-items-end gap-1">
                      <div class="flex-grow-1">
                        <InputField
                          id="email"
                          v-model="customerProfileFormData.email"
                          :label="$t('Email')"
                          :placeholder="$t('Enter email')"
                          type="email"
                        />
                        <p
                          v-if="syncedFields.email"
                          class="sync-info"
                          :class="syncedFields.email === 'synced' ? 'text-success' : ''"
                        >
                          {{ syncedFields.email === 'synced' ? 'Email synced successfully' : '' }}
                        </p>
                      </div>
                    </div>
                  </b-col>
                  <!--  Email -->

                  <!-- Username -->
                  <b-col md="6">
                    <InputField
                      id="username"
                      v-model="customerProfileFormData.userName"
                      :label="$t('Username')"
                      :placeholder="$t('Enter username')"
                      type="text"
                      :error-name="$t('Username')"
                      rules="required"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!--  Username -->
                  <!-- Country -->
                  <b-col md="6">
                    <SelectField
                      id="country"
                      v-model="customerProfileFormData.country"
                      :label="$t('Country')"
                      :options="countryOptions"
                      :reduce="option => option"
                      item="label"
                      :placeholder="$t('Select')"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!--  Country -->
                  <!-- State -->
                  <b-col md="6">
                    <SelectField
                      id="state"
                      v-model="customerProfileFormData.state"
                      :label="$t('State/Province')"
                      :options="stateOptions"
                      :reduce="option => option"
                      item="label"
                      :placeholder="$t('Select')"
                      :set-selected="getLGAByState"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!--  State -->

                  <!-- LGA/Cities -->
                  <b-col md="6">
                    <SelectField
                      id="state"
                      v-model="customerProfileFormData.lga"
                      :label="$t('LGA/Cities')"
                      :options="lgaOptions"
                      :reduce="option => option"
                      item="label"
                      :placeholder="$t('Select')"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!--  LGA -->
                  <!-- Landmark -->
                  <b-col md="6">
                    <InputField
                      id="landmark"
                      v-model="customerProfileFormData.landmark"
                      :label="$t('Landmark')"
                      :placeholder="$t('Enter Landmark')"
                      type="text"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!-- Landmark -->
                  <!--  Address -->
                  <b-col md="12">
                    <TextAreaField
                      id="address"
                      v-model="customerProfileFormData.address"
                      :label="$t('Address')"
                      :placeholder="$t('Enter address')"
                      :error-name="$t('Address')"
                      rules="required"
                      for="address"
                      rows="5"
                      :readonly="isReadonly"
                    />
                    <p
                      v-if="syncedFields.address"
                      class="sync-info"
                      :class="syncedFields.address === 'synced' ? 'text-success' : ''"
                    >
                      {{ syncedFields.address === 'synced' ? 'Address synced successfully' : '' }}
                    </p>
                  </b-col>
                  <!--  Address -->
                </b-row>

                <div class="mt-1">
                  <b-col md="12">
                    <h5 class="section-label mt-3 mb-2">
                      {{ $t('List of Customer Accounts') }}
                    </h5>
                  </b-col>
                  <b-table
                    ref="customerProfileAccountTable"
                    select-mode="single"
                    :items="customerProfileAccountList"
                    responsive
                    :fields="translatedCustomerProfileAccountListHeaders"
                    primary-key="id"
                    selectable
                    show-empty
                    :empty-text="$t('No customer accounts')"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    class="position-relative"
                  >
                    <template #cell(SN)="data">
                      {{ data.index + 1 }}
                    </template>
                    <template #cell(accountTierLevel)="data">
                      {{ data.value ? `Level ${data.value}` : '' }}
                    </template>

                    <template #cell(accountStatus)="data">
                      <b-badge
                        pill
                        :variant="variantColor(data.value)"
                      >
                        {{ data.value }}
                      </b-badge>
                    </template>
                  </b-table>
                </div>
              </b-form>
            </validation-observer>
          </tab-content>

          <!-- Next of Kin Information tab -->
          <tab-content :title="$t('Next of Kin')">
            <validation-observer ref="nextOfKinRules">
              <b-form>
                <h5 class="section-label mb-2">
                  {{ $t('Next of Kin Information') }}
                </h5>
                <b-row class="match-height">
                  <!-- Name -->
                  <b-col md="4">
                    <InputField
                      id="name"
                      v-model="customerProfileFormData.nokName"
                      :label="$t('Name')"
                      :placeholder="$t('Enter name')"
                      type="text"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!-- Name -->

                  <!-- Email -->
                  <b-col md="4">
                    <InputField
                      id="email"
                      v-model="customerProfileFormData.nokEmail"
                      :label="$t('Email')"
                      :placeholder="$t('Enter email')"
                      type="email"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!--  Email -->

                  <!-- Phone Number -->
                  <b-col md="4">
                    <InputField
                      id="phone-number"
                      v-model="customerProfileFormData.nokPhoneNumber"
                      :label="$t('Phone Number')"
                      :placeholder="$t('Enter phone number')"
                      type="number"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!-- Phone Number -->
                  <!-- Relationship -->
                  <b-col md="4">
                    <SelectField
                      id="gender"
                      v-model="customerProfileFormData.nokRelationship"
                      :label="$t('Relationship')"
                      :options="relationshipOptions"
                      :reduce="option => option"
                      item="label"
                      :placeholder="$t('Select')"
                      :readonly="isEditable"
                    />
                    <!-- :set-selected="setRelationship" -->
                  </b-col>
                  <!-- Relationship -->
                  <!--Gender -->
                  <b-col md="4">
                    <SelectField
                      id="gender"
                      v-model="customerProfileFormData.nokGender"
                      :label="$t('Gender')"
                      :options="genderOptions"
                      :reduce="option => option.value"
                      item="label"
                      :placeholder="$t('Select')"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <!-- Gender -->
                </b-row>

                <!--  Address -->
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="next-of-kin-address"
                      v-model="customerProfileFormData.nokAddress"
                      :label="$t('Address')"
                      :placeholder="$t('Enter address')"
                      rows="5"
                      :readonly="isEditable"
                    />
                  </b-col>
                </b-row>
                <!--  Address -->
              </b-form>
            </validation-observer>
          </tab-content>

          <!-- Other Documents tab-->
          <tab-content :title="$t('Other Documents')">
            <validation-observer ref="uploadRules">
              <b-form>
                <h5 class="section-label mb-2">
                  {{ $t('Other Documents') }}
                </h5>
                <div>
                  <div class="w-full mt-4 p-10">
                    <b-row
                      v-for="(item, index) in documentsFormData"
                      :id="index"
                      :key="index"
                      class="mb-2"
                    >
                      <b-col md="6">
                        <SelectField
                          id="document-type"
                          v-model="item.type"
                          :reduce="option => option.value"
                          :label="$t('Document Type')"
                          :placeholder="$t('Select')"
                          :options="translatedDocumentTypeOptions"
                          readonly
                          item="label"
                        />
                      </b-col>
                      <b-col md="7">
                        <label> {{ $t('Upload Documents') }} </label>

                        <div class="upload-image">
                          <Spinner :show="documentLoadSpinner" />
                          <b-form-group>
                            <b-form-file
                              :placeholder="$t('Choose a file or drop it here...')"
                              :drop-placeholder="$t('Drop file here...')"
                              accept="image/jpeg, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              plain
                              disabled
                              @change="onDocumentsUpload(index, $event)"
                            />
                            <div
                              v-if="!item.url"
                              class="upload-instruction"
                            >
                              <h4>{{ $t('Upload Document File') }}</h4>
                              <p>{{ $t('Drag & drop file or choose from your device to upload') }}</p>
                              <p class="format">
                                {{ $t('Format accepted (jpg, png, jpeg, docx, pdf)') }}
                              </p>
                            </div>
                            <div v-else>
                              <div
                                v-if="
                                  item.fileExtension === 'png' ||
                                    item.fileExtension === 'jpeg' ||
                                    item.fileExtension === 'jpg'
                                "
                              >
                                <b-img
                                  :src="item.url"
                                  block
                                  rounded
                                  width="250px"
                                  height="250px"
                                />
                              </div>
                              <div v-if="item.fileExtension === 'pdf'">
                                <b-img
                                  :src="pdfIcon"
                                  block
                                  rounded
                                  width="250px"
                                  height="250px"
                                />
                              </div>
                              <div v-else-if="item.fileExtension === 'vnd'">
                                <b-img
                                  :src="wordIcon"
                                  block
                                  rounded
                                  width="250px"
                                  height="250px"
                                />
                              </div>
                              <div v-else>
                                <b-img src="" />
                              </div>
                              <div
                                v-if="item.url"
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="viewDocumentFile(item.url, item.fileName, item.fileExtension)"
                                />
                              </div>
                              <!-- </div> -->
                            </div>
                          </b-form-group>
                        </div>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <b-col
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(item.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span> {{ $t('Download') }} </span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </b-form>
            </validation-observer>
          </tab-content>

          <template slot="finish">
            <div class="wizard-footer-right" />
          </template>

          <b-modal
            id="info-modal"
            ref="infoModal"
            centered
            size="md"
            ok-only
            :ok-title="$t('Okay')"
            @ok="hideModal('info-modal')"
          >
            <div class="p-2">
              <p>{{ $t('External Customer Reference') }}</p>
            </div>
          </b-modal>
          <b-modal
            id="view-avatar-modal"
            ref="avatarModal"
            no-close-on-backdrop
            :ok-title="$t('Download File')"
            :cancel-title="$t('Cancel')"
            cancel-variant="outline-secondary"
            centered
            size="lg"
            :title="viewDocumentModalTitle"
            @hidden="resetDocumentModal"
            @cancel="resetDocumentModal"
            @ok="handleDownload(viewDocumentData)"
          >
            <div class="m-2 text-center">
              <b-row>
                <b-col cols="12">
                  <div v-if="fileExtension === 'png' || fileExtension === 'jpeg' || fileExtension === 'jpg'">
                    <b-img
                      :src="viewDocumentData"
                      block
                      fluid
                      rounded
                    />
                  </div>
                  <div v-else-if="fileExtension === 'pdf'">
                    <b-img
                      :src="pdfIcon"
                      block
                      width="250px"
                      height="250px"
                      rounded
                    />
                  </div>
                  <div v-else-if="fileExtension === 'vnd'">
                    <b-img
                      :src="wordIcon"
                      block
                      width="250px"
                      height="250px"
                      rounded
                    />
                  </div>
                  <div v-else>
                    <b-img src="" />
                  </div>
                </b-col>
              </b-row>
            </div>
          </b-modal>
          <FullScreenSpinner :show="showSpinner" />
        </form-wizard>
      </div>
      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCustomerProfile"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Customer ID  -->
        <b-col md="6">
          <InputField
            id="customer-id"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter customer ID')"
            type="text"
          />
        </b-col>
        <!-- Customer ID -->
        <b-col md="6">
          <InputField
            id="firstname"
            v-model="filter.firstName"
            :label="$t('First Name')"
            :placeholder="$t('Enter first name')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="last name"
            v-model="filter.lastName"
            :label="$t('Last Name')"
            :placeholder="$t('Enter last name')"
            type="text"
          />
        </b-col>

        <!-- Email  -->
        <b-col md="6">
          <InputField
            id="email"
            v-model="filter.email"
            :label="$t('Email')"
            :placeholder="$t('Enter email')"
            type="email"
          />
        </b-col>
        <!-- Email -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BBadge,
  BForm,
  BButton,
  BFormGroup,
  BFormFile,
  BImg,
  BDropdown,
  BModal,
  BSpinner,
  BDropdownItem,
  VBTooltip,
} from 'bootstrap-vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Spinner from '@/views/components/spinner/Spinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import TextAreaField from '../../../forms/TextAreaField.vue'
import Button from '../../../components/button/Button.vue'
import {
  gender, titleOptions, countries, relationshipOptions, pdfIcon, wordIcon,
} from '../../../../helpers/data'
import { eChannelsPermission } from '../../../../helpers/permissions'

const documentTypeOptions = [
  { value: 1, label: 'Signature Specimen' },
  { value: 2, label: 'Proof Of Address' },
  { value: 3, label: 'Driver’s License' },
  { value: 4, label: 'National Identity Card' },
  { value: 5, label: 'Voters Card' },
  { value: 6, label: 'National Identity Number' },
  { value: 7, label: 'International Passport' },
  { value: 8, label: 'Passport Photograph' },
  { value: 9, label: 'Reference Letter' },
  { value: 10, label: 'Guarantor’s Letter' },
  { value: 11, label: 'Loan Application Form' },
  { value: 12, label: 'Credit Bureau Form' },
  { value: 13, label: 'Others' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BSpinner,
    BDropdownItem,
    BModal,
    BBadge,
    BForm,
    InputField,
    SelectField,
    TablePagination,
    Button,
    TableEntries,
    TextAreaField,
    ValidationObserver,
    FormWizard,
    TabContent,
    BButton,
    BFormGroup,
    BFormFile,
    BImg,
    FullScreenSpinner,
    Spinner,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      syncTitle: '',
      isViewOnly: false,
      isRotating1: false,
      isRotating: false,
      isLoading: false,
      isEditable: false,
      documentsFormData: [
        {
          type: '',
          url: '',
          file: '',
        },
      ],
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      pdfIcon,
      wordIcon,
      documentLoadSpinner: false,
      documentIdentifier: '',
      isDisabled: false,

      toDateWithYear,
      sortBy: 'customerID',
      buttonLabel: this.$t('Save'),
      isSelectLevelThree: false,
      isBusy: false,
      modalTitle: this.$t('Enrolled Customer Profile'),
      required,
      sortDesc: false,
      customerProfileAccountListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountTierLevel', sortable: true, label: 'Account Tier Level' },
        { key: 'accountStatus', label: 'Status' },
      ],
      customerProfileAccountList: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'title', sortable: true, label: 'Title' },
        { key: 'firstName', sortable: true, label: 'First Name' },
        { key: 'lastName', sortable: true, label: 'Last Name' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'userName', sortable: true, label: 'Username' },
        { key: 'dateCreated', sortable: true, label: 'Date Joined' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      relationshipOptions,
      genderOptions: gender,
      titleOptions,
      customerProfileFormData: {
        customerID: '',
        title: '',
        firstName: '',
        lastName: '',
        otherNames: '',
        dateOfBirth: '',
        phoneNumber: '',
        email: '',
        userName: '',
        address: '',
        city: '',
        state: '',
        country: 'Nigeria',
        landmark: '',
        nokName: '',
        nokGender: '',
        nokPhoneNumber: '',
        nokEmail: '',
        nokAddress: '',
        nokRelationship: '',
        id: '',
      },
      originalCustomerProfileFormData: {},
      syncedFields: {},

      isSyncingPhone: false,
      isSyncingEmail: false,
      isSyncingCustomerDetails: false,
      filter: {
        customerID: '',
        firstName: '',
        lastName: '',
        email: '',
      },
      isComponentAlive: true,
      customerProfileList: [],
      countryOptions: ['Nigeria'],
      lgaOptions: [],
    }
  },
  computed: {
    stateOptions() {
      const country = countries.find(item => item.name === 'Nigeria')
      return country.states.map(item => item.state).sort()
    },
    translatedCustomerProfileAccountListHeaders() {
      return this.customerProfileAccountListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedDocumentTypeOptions() {
      return documentTypeOptions.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.value,
      }))
    },
  },
  watch: {
    currentPage() {
      this.fetchCustomerProfile()
    },
    perPage() {
      this.fetchCustomerProfile()
    },
  },
  mounted() {
    this.fetchCustomerProfile()
  },
  beforeDestroy() {
    this.isComponentAlive = false
  },
  methods: {
    ...mapActions({
      getCustomerProfile: 'eChannels/getCustomerProfileList',
      getAccountsByCustomerId: 'eChannels/getAccountsByCustomerId',
      updateCustomerProfile: 'eChannels/updateCustomerProfileAction',
      updateCustomerProfileStatus: 'eChannels/updateCustomerProfileStatus',
      getDocumentFiles: 'account/getDocumentFilesAction',
      resetFailedPasswordCount: 'eChannels/resetFailedPasswordCount',
      getCustomerInformation: 'account/getCustomerInformation',
    }),
    normalizeCustomerProfiles(profiles = []) {
      return profiles.map(items => ({
        ...items,
        email: items.email ? items.email.toLowerCase() : '',
        status: items.isActive === true ? 'Active' : 'InActive',
        dateOfBirth: items.dateOfBirth ? toDateWithYear(items.dateOfBirth) : '',
      }))
    },
    async requestCustomerProfiles(search = {}, extraPayload = {}) {
      return this.getCustomerProfile({
        search,
        permission: eChannelsPermission.VIEW_INTERNET_BANKING_USERS,
        ...extraPayload,
      })
    },

    resetPasswordCount(items) {
      const { id } = items
      const permission = eChannelsPermission.VIEW_INTERNET_BANKING_USERS
      const updatePasswordCount = async () => {
        try {
          this.showSpinner = true
          const formData = {
            id,
          }
          const response = await this.resetFailedPasswordCount({
            formData,
            permission,
          })
          if (response.status === 200) {
            this.alertSuccess({ text: this.$t('Your request to reset password count was successful') })
            this.fetchCustomerProfile()
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      const textValue = this.$t('Are you sure you want to Reset Password Count?')
      this.confirm(updatePasswordCount, { title: '', text: `${textValue}` })
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({ text: this.$t('Your request to download file was successful') })
    },
    getLGAByState(selectedValue) {
      const country = countries.find(item => item.name === 'Nigeria')
      const selectedState = country.states.find(item => item.state === selectedValue)
      this.lgaOptions = selectedState.lgas.sort()
    },
    async fetchCustomerProfile() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        const response = await this.requestCustomerProfiles(this.filter, {
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (!this.isComponentAlive) return
        if (status === 200 && data.data) {
          this.customerProfileList = this.normalizeCustomerProfiles(data.data)
          this.totalCount = data.recordCount
        } else {
          this.customerProfileList = []
          this.totalCount = 0
        }
      } catch (error) {
        if (this.isComponentAlive) {
          this.alertError({ text: error })
        }
      } finally {
        if (this.isComponentAlive) {
          this.isBusy = false
        }
      }
    },
    async resyncCustomerDetails() {
      try {
        this.isSyncingCustomerDetails = true
        const { customerID } = this.customerProfileFormData
        if (!customerID) {
          this.alertError({ text: this.$t('Customer ID is required') })
          return
        }
        const customerResponse = await this.getCustomerInformation({
          customerID,
          permission: eChannelsPermission.VIEW_INTERNET_BANKING_USERS,
        })
        if (!this.isComponentAlive) return
        if (customerResponse.status === 200 && customerResponse.data.data) {
          const hasSyncedFields = this.applySyncedCustomerDetails(customerResponse.data.data)

          this.alertSuccess({
            text: hasSyncedFields
              ? this.$t('Customer details were resynced successfully')
              : this.$t('Customer details are up to date'),
          })
          this.fetchCustomerProfile()
        }
      } catch (error) {
        if (this.isComponentAlive) {
          this.handleError(error)
        }
      } finally {
        if (this.isComponentAlive) {
          this.isSyncingCustomerDetails = false
          this.isRotating = false
          this.isRotating1 = false
        }
      }
    },

    applySyncedCustomerDetails(customerDetails = {}) {
      const fieldsToSync = {
        firstName: customerDetails.firstName || '',
        lastName: customerDetails.lastName || '',
        otherNames: customerDetails.otherNames || '',
        phoneNumber: customerDetails.phoneNumber || '',
        email: customerDetails.email || '',
        address: customerDetails.address || '',
        dateOfBirth: customerDetails.dateOfBirth ? toDateWithYear(customerDetails.dateOfBirth) : '',
      }

      let hasSyncedFields = false // tracks if any field changed

      Object.keys(fieldsToSync).forEach(key => {
        const originalValue = this.originalCustomerProfileFormData[key]
        const syncedValue = fieldsToSync[key]

        if (!originalValue && !syncedValue) {
          this.syncedFields = { ...this.syncedFields, [key]: '' }
          this.customerProfileFormData[key] = fieldsToSync[key]
          return
        }

        if (!syncedValue) {
          this.syncedFields = { ...this.syncedFields, [key]: 'unchanged' }
          this.customerProfileFormData[key] = fieldsToSync[key]
          return
        }

        const isSynced = originalValue !== syncedValue
        if (isSynced) hasSyncedFields = true // at least one field changed

        this.syncedFields = {
          ...this.syncedFields,
          [key]: isSynced ? 'synced' : 'unchanged',
        }
        this.customerProfileFormData[key] = fieldsToSync[key]
      })

      return hasSyncedFields
    },

    async getDocumentByDocumentIdentifier(documentIdentifier) {
      try {
        const response = await this.getDocumentFiles({
          referenceNumber: documentIdentifier,
          permission: eChannelsPermission.VIEW_INTERNET_BANKING_USERS,
        })
        const { status, data } = response
        if (!this.isComponentAlive) return
        if (status === 200 && data.data) {
          this.documentsFormData = data.data
            ? data.data.map(item => ({
              url: item.url,
              type: item.typeID,
              fileName: item.type,
              id: item.id,
              fileExtension: item.fileExtension,
            }))
            : [
              {
                url: '',
                type: '',
              },
            ]
        }
      } catch (error) {
        if (this.isComponentAlive) {
          this.alertError({ text: error })
        }
      } finally {
        if (this.isComponentAlive) {
          this.isBusy = false
        }
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.enrollCustomerProfileRules.validate().then(async success => {
          if (success) {
            const areYouSure = this.$t('Are you sure you want to')
            const thisText = this.$t('this Enrolled Customer Profile?')
            this.confirm(this.customerProfileAction, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-lg')
      }
    },
    async customerProfileAction() {
      try {
        this.isLoading = true
        const formData = {
          firstName: this.customerProfileFormData.firstName,
          lastName: this.customerProfileFormData.lastName,
          otherNames: this.customerProfileFormData.otherNames,
          phoneNumber: this.customerProfileFormData.phoneNumber,
          email: this.customerProfileFormData.email,
          userName: this.customerProfileFormData.userName,
          address: this.customerProfileFormData.address,
          id: this.customerProfileFormData.id,
        }
        const response = await this.updateCustomerProfile({
          formData,
          permission: eChannelsPermission.UPDATE_IB_PROFILE,
        })
        const { status } = response
        if (status === 200) {
          this.fetchCustomerProfile()
          this.hideModal('modal-lg')
          const text1 = this.$t('Your request to')
          const text2 = this.$t('Enrolled Customer Profile was successful')
          this.alertSuccess({ text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    productProfileStatus(items) {
      const {
        id, lastName, firstName, email, phoneNumber, customerID, isActive,
      } = items
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? eChannelsPermission.DEACTIVATE_IB_PROFILE : eChannelsPermission.ACTIVATE_IB_PROFILE

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            lastName,
            firstName,
            email,
            phoneNumber,
            customerID,
            isActive,
          }
          const response = await this.updateCustomerProfileStatus({
            statusValues,
            routeName: action,
            permission,
          })
          if (response.status === 200) {
            this.fetchCustomerProfile()
            const text1 = this.$t('Your request to')
            const successText = this.$t('this Enrolled Customer Profile was successful')
            this.alertSuccess({ text: `${text1} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const text1 = this.$t('Your request to')
          const text2 = this.$t('this Enrolled Customer Profile failed. Reason:')
          this.alertError({ text: `${text1} ${action} ${text2} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const thisText = this.$t('this Enrolled Customer Profile?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
    },

    async onSelectProductProfileItem(items, activity, val) {
      try {
        const response = await this.getAccountsByCustomerId({ customerId: items.customerID })
        const { data } = response
        this.customerProfileAccountList = data.data
        this.customerProfileFormData = {
          ...items,
          dateOfBirth: items.dateOfBirth ? toDateWithYear(items.dateOfBirth) : '',
        }

        this.originalCustomerProfileFormData = {
          ...items,
        }
        this.syncedFields = {}

        this.getDocumentByDocumentIdentifier(items.documentIdentifier)
        this.isViewOnly = val === 'view'
        this.isEditable = true
        this.isReadonly = val === 'edit' ? (this.isReadonly = false) : (this.isReadonly = true)
        this.modalTitle = val === 'edit' ? this.$t('Edit Enrolled Customer Profile') : this.$t('View Enrolled Customer Profile')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCustomerProfile()
    },
    resetModal() {
      this.customerProfileFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.buttonLabel = this.$t('Save')
    },
  },
}
</script>
<style lang="scss" scoped>
.upload-image {
  border: 2px dashed #dcd9d9;
  position: relative;
  width: 250px;
  height: 250px;
}

.format {
  color: #999999 !important;
}

form input[type='file'] {
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}

.upload-instruction {
  padding-top: 22%;
  color: #000;
  text-align: center;
}

.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}

.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}

.rotate-icon {
  animation: rotate-once 0.5s ease-in-out;
}

@keyframes rotate-once {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
.sync-info {
  font-size: 12px;
  margin-top: -10px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/base/bootstrap-extended/_include.scss';
</style>
